import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Download, Plus } from "lucide-react";
import { reportsApi, downloadBlob } from "@/api/reports";
import type { GeneratedReportDto } from "@/api/reports";
import { Badge, Button } from "@/design-system/components/ui";
import { DataTable, type Column } from "@/components/layout/DataTable";
import { PageHeader } from "@/components/layout/PageHeader";
import { StageBadge } from "@/components/reports/v32/StageBadge";
import { useAuth } from "@/hooks/useAuth";
import { useDateFormatter } from "@/utils/datetime";

export function ReportsPage() {
  const { hasPermission } = useAuth();
  const canGenerate = hasPermission("Reports.Generate");
  const canDownload = hasPermission("Reports.Download");
  const { dateTime } = useDateFormatter();

  const list = useQuery({
    queryKey: ["reports"],
    queryFn: () => reportsApi.list({ page: 1, pageSize: 100 }),
  });

  const columns: Column<GeneratedReportDto>[] = [
    {
      key: "title",
      header: "Report",
      render: (r) => (
        <Link to={`/reports/${r.id}`} className="font-semibold hover:text-gold-shadow">
          {r.title}
        </Link>
      ),
    },
    { key: "template", header: "From template", render: (r) => r.promptTemplateName },
    { key: "output", header: "Format", render: (r) => <Badge status="info">{r.outputFormat}</Badge> },
    {
      key: "stage",
      header: "Stage",
      render: (r) => <StageBadge stage={r.generationStage ?? null} />,
    },
    { key: "created", header: "Created", render: (r) => dateTime(r.createdAt) },
    {
      key: "actions",
      header: "",
      render: (r) => (
        <div className="flex items-center gap-space-2 justify-end">
          {canDownload && (
            <button
              type="button"
              className="p-space-1 rounded-md text-gold-shadow hover:bg-white-warm"
              aria-label={`Download ${r.title}`}
              onClick={async () => {
                const blob = await reportsApi.exportDocx(r.id);
                downloadBlob(blob, `${r.title.replace(/[^a-z0-9-_]/gi, "-")}.docx`);
              }}
            >
              <Download size={16} aria-hidden />
            </button>
          )}
        </div>
      ),
    },
  ];

  return (
    <>
      <PageHeader
        eyebrow="Tools"
        title="Reports"
        description="Generate LLM reports, form-fill documents, and interactive form reports. Every generation is logged with input/output tokens and cost."
        actions={
          canGenerate && (
            <Link to="/reports/generate">
              <Button>
                <Plus size={16} aria-hidden /> Generate report
              </Button>
            </Link>
          )
        }
      />

      <DataTable
        columns={columns}
        rows={list.data?.data ?? []}
        loading={list.isLoading}
        error={list.isError ? (list.error as Error).message : null}
        emptyMessage="No reports yet. Click 'Generate report' to create your first one."
        rowKey={(r) => r.id}
      />
    </>
  );
}
